import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SITE } from "@/data/content";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FAF7F7] grid place-items-center px-6">
    <div className="max-w-md w-full bg-white rounded-[2rem] border border-rose-100 p-10 text-center shadow-[0_30px_80px_-50px_rgba(159,18,57,0.4)]">
      {children}
    </div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (++tries < 12) setTimeout(poll, 1500);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <Shell>
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-rose-700 animate-spin mx-auto" />
          <h1 className="font-display text-3xl text-stone-900 mt-6">A confirmar o pagamento…</h1>
          <p className="text-stone-500 mt-3 text-sm">Aguarde um instante, por favor.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-rose-700 mx-auto" />
          <h1 className="font-display text-3xl text-stone-900 mt-6">Marcação confirmada!</h1>
          <p className="text-stone-600 mt-3 text-sm leading-relaxed">
            Obrigada pela sua confiança. Receberá em breve os detalhes por email. Até breve. 🌸
          </p>
          <Link to="/" className="btn-rose inline-flex mt-8 rounded-full bg-rose-800 text-white px-8 py-3 text-sm">
            Voltar ao início
          </Link>
        </>
      )}
      {(status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-14 h-14 text-stone-400 mx-auto" />
          <h1 className="font-display text-3xl text-stone-900 mt-6">
            {status === "timeout" ? "Ainda a processar" : "Algo correu mal"}
          </h1>
          <p className="text-stone-600 mt-3 text-sm">
            {status === "timeout"
              ? "O pagamento pode demorar alguns instantes. Verifique o seu email ou contacte-nos."
              : "Não foi possível confirmar o pagamento."}{" "}
            {SITE.email}
          </p>
          <Link to="/marcacao" className="inline-flex mt-8 rounded-full border border-stone-200 px-8 py-3 text-sm text-stone-700">
            Tentar novamente
          </Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancelled() {
  return (
    <Shell>
      <XCircle className="w-14 h-14 text-stone-400 mx-auto" />
      <h1 className="font-display text-3xl text-stone-900 mt-6">Pagamento cancelado</h1>
      <p className="text-stone-600 mt-3 text-sm">A sua marcação não foi concluída. Pode tentar novamente quando quiser.</p>
      <Link to="/marcacao" className="btn-rose inline-flex mt-8 rounded-full bg-rose-800 text-white px-8 py-3 text-sm">
        Voltar à marcação
      </Link>
    </Shell>
  );
}

export function FreeConfirmed() {
  return (
    <Shell>
      <CheckCircle2 className="w-14 h-14 text-rose-700 mx-auto" />
      <h1 className="font-display text-3xl text-stone-900 mt-6">Marcação confirmada!</h1>
      <p className="text-stone-600 mt-3 text-sm leading-relaxed">
        A sua consulta está agendada. O pagamento será efetuado no local, no dia da consulta.
        Receberá em breve os detalhes por email. Até breve. 🌸
      </p>
      <Link to="/" className="btn-rose inline-flex mt-8 rounded-full bg-rose-800 text-white px-8 py-3 text-sm items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Voltar ao início
      </Link>
    </Shell>
  );
}
